import React, { useMemo } from "react";
import {
  createSearchParams,
  Navigate,
  NavLink,
  NavLinkProps,
  useLocation,
} from "react-router";
import { AnyObject } from "@triare/auth-redux";
import {
  ContainerOutlined,
  FileTextOutlined,
  FormOutlined,
  InboxOutlined,
  TagsOutlined,
  TeamOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { Menu as AntdMenu } from "antd";
import { connect } from "react-redux";
import { Route } from "../../../routes";
import store, { RootState } from "../../../store";
import { moduleName, useAuth, User } from "../../../store/auth";
import { doesUserHaveAccess } from "../../../routes/PrivateRoute";
import i18n from "../../../i18n";
import { useTranslation } from "react-i18next";
import Users from "../../../pages/Users";
import UsersCreate from "../../../pages/Users/create";
import UsersEdit from "../../../pages/Users/edit";
import Survey from "../../../pages/Survey";
import SurveyCreate from "../../../pages/Survey/create";
import SurveyEdit from "../../../pages/Survey/edit";

import styles from "./index.module.scss";
import FieldTechnicians from "../../../pages/FieldTechnicians";
import FieldTechniciansEdit from "../../../pages/FieldTechnicians/edit";
import Templates from "../../../pages/Templates";
import TemplateCreate from "../../../pages/Templates/create";
import TemplateEdit from "../../../pages/Templates/edit";
import TemplateView from "../../../pages/Templates/view";
import SurveyResponses from "../../../pages/SurveyResponses";
import SurveyResponsesEdit from "../../../pages/SurveyResponses/edit";
import PhotoTags from "../../../pages/PhotoTags";

export interface MenuRoute extends Route {
  ignoreMenu?: boolean;

  children?: MenuRoute[];
  /** If translation key passed - menu will use text from i18n config. If not - use 'name' from route */
  translationKey?: string;
}

export const routes: MenuRoute[] = [
  {
    privateRoute: true,
    name: "Managers",
    // translationKey: "routes.users",
    align: "left",
    icon: <UserOutlined />,
    roles: ["root"],
    route: {
      path: "users",
      element: <Users />,
    },
    children: [
      {
        ignoreMenu: true,
        privateRoute: true,
        name: "Create Users",
        route: {
          path: "create",
          element: <UsersCreate />,
        },
      },
      {
        ignoreMenu: true,
        privateRoute: true,
        name: "Users redirect to edit",
        route: {
          path: ":id",
          element: <Navigate to="edit" replace />,
        },
        children: [
          {
            ignoreMenu: true,
            privateRoute: true,
            name: "Edit Users",
            route: {
              path: "edit",
              element: <UsersEdit />,
            },
          },
        ],
      },
    ],
  },
  {
    privateRoute: true,
    name: "Field Technicians",
    // translationKey: "routes.users",
    align: "left",
    icon: <TeamOutlined />,
    // roles: ["root"],
    route: {
      path: "field-technicians",
      element: <FieldTechnicians />,
    },
    children: [
      {
        ignoreMenu: true,
        privateRoute: true,
        name: "Field Technicians redirect to edit",
        route: {
          path: ":id",
          element: <Navigate to="edit" replace />,
        },
        children: [
          {
            ignoreMenu: true,
            privateRoute: true,
            name: "Edit Field Technicians",
            route: {
              path: "edit",
              element: <FieldTechniciansEdit />,
            },
          },
        ],
      },
    ],
  },
  {
    privateRoute: true,
    name: "Surveys",
    translationKey: "Surveys",
    align: "left",
    icon: <FormOutlined />,
    route: {
      path: "survey",
      element: <Survey />,
    },
    children: [
      {
        ignoreMenu: true,
        privateRoute: true,
        name: "Create Survey",
        route: {
          path: "create",
          element: <SurveyCreate />,
        },
      },
      {
        ignoreMenu: true,
        privateRoute: true,
        name: "Survey redirect to edit",
        route: {
          path: ":id",
          element: <Navigate to="edit" replace />,
        },
        children: [
          {
            ignoreMenu: true,
            privateRoute: true,
            name: "Edit Survey",
            route: {
              path: "edit",
              element: <SurveyEdit />,
            },
          },
        ],
      },
    ],
  },
  {
    privateRoute: true,
    name: "Templates",
    translationKey: "Templates",
    align: "left",
    icon: <FileTextOutlined />,
    route: {
      path: "templates",
      element: <Templates />,
    },
    children: [
      {
        ignoreMenu: true,
        privateRoute: true,
        name: "Templates Survey",
        route: {
          path: "create",
          element: <TemplateCreate />,
        },
      },
      {
        ignoreMenu: true,
        privateRoute: true,
        name: "Templates redirect to edit",
        route: {
          path: ":id",
          element: <Navigate to="edit" replace />,
        },
        children: [
          {
            ignoreMenu: true,
            privateRoute: true,
            name: "Templates Survey",
            route: {
              path: "edit",
              element: <TemplateEdit />,
            },
          },
        ],
      },
      {
        ignoreMenu: true,
        privateRoute: true,
        name: "Templates redirect to view",
        route: {
          path: ":id",
          element: <Navigate to="view" replace />,
        },
        children: [
          {
            privateRoute: true,
            ignoreMenu: true,
            name: "Templates Survey",
            route: {
              path: "view",
              element: <TemplateView />,
            },
          },
        ],
      },
    ],
  },
  {
    privateRoute: true,
    name: "Responses",
    translationKey: "Responses",
    align: "left",
    icon: <ContainerOutlined />,
    route: {
      path: "survey-responses",
      element: <SurveyResponses />,
    },
    children: [
      {
        ignoreMenu: true,
        privateRoute: true,
        name: "Survey responses redirect to edit",
        route: {
          path: ":id",
          element: <Navigate to="edit" replace />,
        },
        children: [
          {
            ignoreMenu: true,
            privateRoute: true,
            name: "Edit Response",
            route: {
              path: "edit",
              element: <SurveyResponsesEdit />,
            },
          },
        ],
      },
    ],
  },
  {
    privateRoute: true,
    name: "Photo Tags",
    // translationKey: "routes.users",
    align: "left",
    icon: <TagsOutlined />,
    route: {
      path: "photo-tags",
      element: <PhotoTags />,
    },
  },
];

export const paramsToString = (
  user?: User | null,
  params?: ((user: User) => string) | AnyObject | string,
) => {
  if (!user) {
    return "";
  }

  if (typeof params === "string") {
    return "?params";
  }

  if (typeof params === "function") {
    let result = params(user);

    if (result) {
      if (typeof result === "object") {
        result = createSearchParams(result).toString();
      }

      return `?${result}`;
    }

    return "";
  }

  if (params && typeof params === "object") {
    return `?${createSearchParams(params).toString()}`;
  }

  return "";
};

const createItem = (
  {
    route,
    children,
    ignoreMenu,
    privateRoute,
    translationKey,
    ...data
  }: MenuRoute, // Route,
  user: User,
) => {
  let childrenList;

  const navChildGeneralProps: Partial<NavLinkProps> = {
    className: "clip",
    style: {
      maxWidth: "var(--menu-item-max-w)",
      // display: "flex",
      // alignItems: "center",
    },
  };

  // && children.every((item) => !item.hidden)
  if (children && children.length > 0) {
    childrenList = children.filter(
      ({ ignoreMenu: childrenIgnoreMenu }) => childrenIgnoreMenu !== true,
    );

    if (childrenList.length > 0) {
      childrenList = childrenList.map(
        ({
          ignoreMenu: childrenIgnoreMenu,
          privateRoute: childrenPrivateRoute,
          ...child
        }) => {
          const childName =
            typeof child.name === "function" ? child.name(user) : child.name;

          /** If translation key passed - menu will use text from i18n config. If not - use 'name' from route */
          const translatedChildName = child.translationKey
            ? i18n.t(child.translationKey)
            : childName;

          return {
            ...child,
            children: undefined,
            label: child.route ? (
              <NavLink
                to={`/${child.route.path}${paramsToString(user, child.route.path) || ""}`}
                title={translatedChildName} // {child.name || child.key}
                {...navChildGeneralProps}
              >
                {/*{child.name || child.key}*/}
                {translatedChildName}
              </NavLink>
            ) : (
              child.name
            ),
            key: child.route
              ? `/${child.route.path}`
              : `/${child.key || child.name}`,
          };
        },
      );
    }
  }

  const name = typeof data.name === "function" ? data.name(user) : data.name;

  const translatedName = translationKey ? i18n.t(translationKey) : name;

  return {
    ...data,
    children:
      childrenList && childrenList.length > 0 ? childrenList : undefined,
    label:
      route && (childrenList || []).length === 0 ? (
        <NavLink
          to={`/${route.path}${paramsToString(user, route.params) || ""}`}
          title={translatedName} // name
          {...navChildGeneralProps}
        >
          {translatedName}
          {/*{name}*/}
        </NavLink>
      ) : (
        translatedName // name
      ),
    key: route ? `/${route.path}` : `/${data.key || name}`,
  };
};

export const menuLeft = routes
  .filter(({ ignoreMenu }) => ignoreMenu !== true)
  .filter(({ align }) => align === "left");

export const menuRight = routes
  .filter(({ ignoreMenu }) => ignoreMenu !== true)
  .filter(({ align }) => align === "right");

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function filter(list: any[], user: User) {
  const newList = list.map((item) => ({
    ...item,
    children: item.children ? [...item.children] : undefined,
  }));

  return newList.filter((item) => {
    if (item && item.children && item.children.length > 0) {
      item.children = filter(item.children, user);
    }

    return item?.roles ? doesUserHaveAccess(item?.roles, user) : true;
  });
}

export interface MenuProps {
  user: User;
}

function Menu({ user }: MenuProps): React.ReactNode {
  const { authorized } = useAuth();
  // const { open, contextHolder } = useSimpleModal();
  const { pathname } = useLocation();
  const { i18n } = useTranslation();

  const currentLang = i18n.language;
  const menuLeftFiltered = useMemo(
    () =>
      filter(
        menuLeft.map((data) => createItem(data, user)),
        user,
      ).map((item) => ({
        ...item,
        name: typeof item.name === "function" ? item.name(user) : item.name,
        icon: typeof item.icon === "function" ? item.icon(user) : item.icon,
      })), // .map(({ children, ...item }) => item)
    [user, currentLang],
  );
  const menuRightFiltered = useMemo(() => {
    const list = filter(
      menuRight.map((data) => createItem(data, user)),
      user,
    ).map((item) => ({
      ...item,
      name: typeof item.name === "function" ? item.name(user) : item.name,
      icon: typeof item.icon === "function" ? item.icon(user) : item.icon,
    }));

    // if (list[2] && list[2].children && list[2].children[3]) {
    //   list[2].children[3].onClick = () => {
    //     open({
    //       icon: <LogoutOutlined style={{ color: "#FF4D4F" }} />,
    //       title: "Log out",
    //       content: "Are you sure you want to Log out?",
    //       cancelText: "Cancel",
    //       centered: true,
    //       okText: "Yes",
    //       onOk: () => {
    //         store.dispatch(signOut());
    //         navigate("/");
    //       },
    //     });
    //   };
    // }

    return list;
  }, [user, currentLang]);

  const activeMenuItem = `/${pathname.split("/")[1]}`;

  return (
    <div className={styles.menu}>
      <AntdMenu
        key={`top-${activeMenuItem}`}
        theme="light"
        mode="inline" // "vertical"
        defaultSelectedKeys={[activeMenuItem]}
        selectedKeys={[activeMenuItem]}
        items={menuLeftFiltered}
      />
      {authorized ? (
        <AntdMenu
          key={`bottom-${activeMenuItem}`}
          className={styles.rightMenu}
          theme="light"
          mode="vertical"
          defaultSelectedKeys={[activeMenuItem]}
          selectedKeys={[activeMenuItem]}
          items={menuRightFiltered}
        />
      ) : (
        <div style={{ padding: 8 }}>Not authorized</div>
      )}
    </div>
  );
}

export default connect((state: RootState) => ({
  user: state[moduleName].user,
}))(Menu);
